/**
 * OwnContactSwitch (KAN-723): "Свой контакт (тестовый)" switch.
 *
 * An own contact (the owner's test Telegram account and the like) is hidden
 * from "Требуют внимания" and is not counted in the funnel; it stays in the
 * clients list with the "Свой" badge (OwnContactBadge). The change is saved
 * right away (PATCH /clients/{id}/ is_own) via useSetClientOwn.
 *
 * Used in the status card of the client page and in the transcript modal
 * header.
 */
import { useId } from 'react';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useSetClientOwn } from '@/services/api/clientQueries';

export const OWN_CONTACT_LABEL = 'Свой контакт (тестовый)';
export const OWN_CONTACT_HINT =
  'не показывается в Требуют внимания и не учитывается в воронке';

interface OwnContactSwitchProps {
  clientId: string;
  isOwn: boolean;
}

export function OwnContactSwitch({ clientId, isOwn }: OwnContactSwitchProps): JSX.Element {
  const switchId = useId();
  const setOwn = useSetClientOwn(clientId);
  // While the PATCH is in flight show the requested value, not the stale card.
  const checked = setOwn.isPending && setOwn.variables !== undefined ? setOwn.variables : isOwn;

  return (
    <div className="flex items-start gap-2 text-sm" data-testid="own-contact">
      <Switch
        id={switchId}
        checked={checked}
        onCheckedChange={(value) => setOwn.mutate(value)}
        disabled={setOwn.isPending}
        data-testid="own-contact-switch"
      />
      <div className="min-w-0">
        <label htmlFor={switchId} className="cursor-pointer font-medium text-gray-900">
          {OWN_CONTACT_LABEL}
        </label>
        <p className="text-xs text-gray-500">{OWN_CONTACT_HINT}</p>
      </div>
    </div>
  );
}

/** "Свой" badge next to the client name in lists. */
export function OwnContactBadge(): JSX.Element {
  return (
    <Badge
      variant="outline"
      className="shrink-0 whitespace-nowrap border-amber-300 bg-amber-50 px-1.5 py-0 text-[10px] leading-4 text-amber-800"
      title={OWN_CONTACT_HINT}
      data-testid="own-contact-badge"
    >
      Свой
    </Badge>
  );
}
